const ZERO_DIVE_CATEGORIES = new Set(["Nitrox"]);

export function formatNumberOfDives(
  numberOfDives: number | null | undefined,
  category?: string | null
): string {
  if (numberOfDives === null || numberOfDives === undefined) {
    if (category && ZERO_DIVE_CATEGORIES.has(category)) {
      return "0 dives";
    }
    return "-";
  }

  return `${numberOfDives} ${numberOfDives === 1 ? "dive" : "dives"}`;
}

export function formatListingBoat(
  boat: string | null | undefined,
  numberOfDives: number | null | undefined,
  category?: string | null
): string {
  if (!boat) {
    return "-";
  }

  const dives =
    numberOfDives ??
    (category && ZERO_DIVE_CATEGORIES.has(category) ? 0 : null);

  const boatLabel = boat === "both" ? "Boat & shore" : boat;
  if (dives === null) {
    return boatLabel;
  }

  return `${boatLabel} ${dives === 1 ? "dive" : "dives"}`;
}

export function parseListingItems(
  value: string | null | undefined,
  options?: { category?: string | null }
): string[] {
  const items = parseListingItemsRaw(value);

  if (options?.category === "Nitrox") {
    return items.filter((item) => item.toLowerCase() !== "nitrox");
  }

  return items;
}

function parseListingItemsRaw(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];

  for (const part of value.split(",")) {
    const item = part.trim();
    if (!item) continue;

    const key = item.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    result.push(item);
  }

  return result;
}

export function formatListingItems(items: string[]): string {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of items) {
    const trimmed = item.trim();
    if (!trimmed) continue;

    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    result.push(trimmed);
  }

  return result.join(", ");
}

export function removeListingItems(
  value: string | null | undefined,
  itemsToRemove: string[]
): string {
  const removeKeys = new Set(
    itemsToRemove.map((item) => item.trim().toLowerCase()).filter(Boolean)
  );

  return formatListingItems(
    parseListingItems(value).filter((item) => !removeKeys.has(item.toLowerCase()))
  );
}

/**
 * Lists are stored as one comma-separated string, so a comma inside a custom
 * item would split it in two ("Rp 150,000" -> "Rp 150" + "000").
 * Thousands separators become dots and any other comma becomes " /".
 */
export function sanitizeCustomListingItem(value: string): string {
  return value
    .trim()
    .replace(/(\d),(?=\d{3}\b)/g, "$1.")
    .replace(/\s*,\s*/g, " / ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Items saved on the listing that are not one of the fixed checkbox options. */
export function customListingItems(items: string[], presets: string[]): string[] {
  const presetKeys = new Set(presets.map((p) => p.toLowerCase()));
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.toLowerCase();
    if (presetKeys.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
